import { StrictMode, Suspense, useEffect, useState, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { getWrapperRole, setWrapperRoleFromAppId } from './utils/wrapperRole'
import '@fontsource-variable/plus-jakarta-sans/wght.css'
import '@fontsource/ibm-plex-mono/latin-400.css'
import '@fontsource/ibm-plex-mono/latin-ext-400.css'
import '@fontsource/ibm-plex-mono/latin-500.css'
import '@fontsource/ibm-plex-mono/latin-ext-500.css'
import '@fontsource/ibm-plex-mono/latin-600.css'
import '@fontsource/ibm-plex-mono/latin-ext-600.css'
import './index.css'
import './utils/documentVisibilityBridge'
import App from './App.tsx'
import { AppMember, parseMemberRoute, type MemberRoute } from './member/AppMember.tsx'
import './member/member.css'
import './components/Common/panelPrimitives.css'
import './components/Common/premiumSurface.css'
import './components/Member/appRouteMessage.css'
import { activateDebugBypassFromUrl, isNoSignInPreviewActive } from './debug'
import { GlobalNativeTitleUpgrader } from './components/Common/Hover'
import { HelperInviteLanding } from './components/Helper/HelperInviteLanding.tsx'
import { FieldPassLanding } from './components/FieldPass/FieldPassLanding.tsx'
import { ApprovalLanding } from './components/OutsideApproval/ApprovalLanding.tsx'
import { ClaimLanding } from './components/LostClaim/ClaimLanding.tsx'
import { BmwHandoverLanding } from './components/BmwHandover/BmwHandoverLanding.tsx'
import { SampleHandoverLanding } from './components/SampleHandover/SampleHandoverLanding.tsx'
import { TeleConsentLanding } from './components/TeleConsent/TeleConsentLanding.tsx'
import { OwnerFeedLanding } from './components/OwnerFeed/OwnerFeedLanding.tsx'
import { ClientLinkLanding } from './components/ClientLink/ClientLinkLanding.tsx'
import { extractClientLinkToken } from './utils/clientLinkRoute'
import { SharedDeviceShell } from './components/SharedDevice/SharedDeviceShell.tsx'
import { CertificateVerifyPage } from './components/Verify/CertificateVerifyPage.tsx'
import { Login } from './components/Auth/Login.tsx'
import { OnboardingGate } from './components/Onboarding/OnboardingGate.tsx'
import { ProfileDebugOverlay } from './components/Profile/ProfileDebugOverlay.tsx'
import { Button } from './components/Common/Button'
import { AccessDenied } from './components/Common/AccessDenied.tsx'
import { FullscreenLoader } from './components/Common/FullscreenLoader.tsx'
import { ConstellationSplash } from './components/Common/ConstellationSplash.tsx'
import { AlivenessBridge } from './components/Aliveness/AlivenessBridge.tsx'
import { DevActionsPanel } from './components/dev/DevActionsPanel.tsx'

// Splash surfaces are now vector-based via <FullscreenLoader> — see
// src/components/Common/Loader.tsx. Boot uses the constellation variant;
// workspace-setup uses the growing-timeline variant.
import { AuthProvider } from './context/AuthContext.tsx'
import { ClientPreferencesProvider } from './context/ClientPreferencesContext.tsx'
import { PreviewProvider } from './context/PreviewContext.tsx'
import { PreviewStatusBar } from './components/Common/PreviewStatusBar.tsx'
import { ApiConnectivityProvider } from './components/Common/ApiConnectivityOverlay.tsx'
import { GlobalActionLoadingProvider } from './context/GlobalActionLoadingContext.tsx'
import { NotificationProvider } from './context/NotificationContext.tsx'
import { ToastProvider } from './context/ToastContext.tsx'
import { ToastViewport } from './components/Toast/ToastViewport.tsx'
import { MaintenanceBanner } from './components/Common/MaintenanceBanner.tsx'
import { WorkspaceAccessLostSheet } from './components/Common/WorkspaceAccessLostSheet'
import { ConsentNoticeDialog } from './components/Common/ConsentNoticeDialog'
import { useIsAccessLostPromptActive } from './services/workspaceSwitch/accessLostPrompt'
import { WorkspaceScopedProviders } from './context/WorkspaceScopedProviders.tsx'
import { requestWorkspaceSwitch } from './services/workspaceSwitchService.ts'
import { buildUrlRestoreOnStay } from './services/workspaceSwitch/switchLanding.ts'
import { useAuth } from './context/useAuth.ts'
import { WorkspaceProvider } from './context/WorkspaceContext.tsx'
import { CreditsProvider } from './context/CreditsContext.tsx'
import { SubscriptionProvider } from './context/SubscriptionContext.tsx'
import { DeferredEventOutboxBridge } from './components/DeferredEventOutboxBridge.tsx'
import { LineLearningSignalFlushBridge } from './components/LineLearningSignalFlushBridge.tsx'
import { AutomationDispatchBridge } from './components/Automate/AutomationDispatchProvider.tsx'
import './services/actions/domains'
import { ProductAnalyticsRouteBridge } from './components/Analytics/ProductAnalyticsRouteBridge.tsx'
import { DebugSessionRecorder } from './components/Debug/DebugSessionRecorder.tsx'
import { DebugStateProvidersBridge } from './components/Debug/DebugStateProvidersBridge.tsx'
import { NlsProvider, applyStoredNlsLocaleToDocument, useNls } from './nls/nlsProvider.tsx'
import { useWorkspace } from './context/useWorkspace.ts'
import { parseJoinToken, stashPendingLinkToken } from './services/inviteService.ts'
import { MemberRoleGuard } from './components/Member/MemberRoleGuard.tsx'
import { MobileUpdateBridge } from './components/Mobile/MobileUpdateBridge.tsx'
import { InviteReviewPage } from './components/InviteReviewPage.tsx'
import { JoinWithCodePage } from './components/JoinCodes/JoinWithCodePage.tsx'
import { PublicSite, PUBLIC_SITE_PATHS } from './public/PublicSite.tsx'
import { NotFoundPage } from './components/NotFoundPage.tsx'
import {
  classifyTopLevelRoute,
  shouldStashForPostLogin,
  detectVerifyRoute,
  detectVerifyEvidenceRoute,
  detectAccountTransferConfirmRoute,
  type VerifyRouteState,
  type AccountTransferConfirmRouteState,
} from './utils/topLevelRouteClassifier.ts'
import { VerifyEvidencePage } from './public/evidence/VerifyEvidencePage.tsx'
import { isHelperOnlyAccount } from './utils/helperMembership.ts'
import { AccountTransferConfirmPage } from './components/AccountTransfer/AccountTransferConfirmPage.tsx'
import { stashPendingRedirect } from './services/pendingRedirect.ts'
import { OwnerConsole } from './components/OwnerConsole/OwnerConsole.tsx'
import { TelemetryBoundary } from './components/TelemetryBoundary.tsx'
import { telemetryLogger } from './services/telemetryLogger.ts'
import { BOOT_CONSTELLATION_SPLASH_TIMING } from './config/splashTiming.ts'
import {
  installConsoleHistoryAdapter,
  normalizeLegacyConsoleUrl,
} from './utils/consoleRoutes.ts'
import { installStaleChunkReloadHandler, lazyWithStaleChunkRetry } from './utils/lazyWithStaleChunkRetry.ts'
import {
  getNativeInitialRoute,
  resolveMemberShellFallbackPath,
  resolveNativeRouteLockRedirect,
} from './utils/nativeRouteIsolation.ts'
import { openWorkspaceTarget } from './services/workspaceSwitch/openWorkspaceTarget.ts'
import { isPersonalWorkspace, isTeamWorkspace, resolvePersonalForUser } from './utils/workspaceKind.ts'
import { isSharedDevicePath } from './utils/sharedDeviceRoute.ts'
import { featureFlag } from './utils/featureFlags.ts'
import { startFeatureFlagSync } from './services/featureFlagsService.ts'
import { buildMemberPath, PERSONAL_SLUG_SEGMENT } from './member/memberAppRouting.ts'

import { FamilyRoute } from './components/Family/FamilyRoute.tsx'
import { EduPtmJoinRoute } from './components/Family/EduPtmJoinRoute.tsx'
import { useIsFamilyOnly } from './hooks/useFamilyAccess.ts'
import { resolveFamilyConsoleRedirect } from './utils/familyRedirect.ts'

const SharedReport = lazyWithStaleChunkRetry('SharedReport', () => import('./components/Shared/SharedReport').then((module) => ({ default: module.SharedReport })))
const SharedReportCard = lazyWithStaleChunkRetry('SharedReportCard', () => import('./components/Shared/SharedReportCard').then((module) => ({ default: module.SharedReportCard })))
const ClientOptOutPage = lazyWithStaleChunkRetry('ClientOptOutPage', () => import('./components/Shared/ClientOptOutPage').then((module) => ({ default: module.ClientOptOutPage })))
const ClientFormPage = lazyWithStaleChunkRetry('ClientFormPage', () => import('./components/Shared/ClientFormPage').then((module) => ({ default: module.ClientFormPage })))
const ClientQuotePage = lazyWithStaleChunkRetry('ClientQuotePage', () => import('./components/Shared/ClientQuotePage').then((module) => ({ default: module.ClientQuotePage })))
const PlaceScanPage = lazyWithStaleChunkRetry('PlaceScanPage', () => import('./components/Ops/scan/PlaceScanPage').then((module) => ({ default: module.PlaceScanPage })))
const GuestRoomPage = lazyWithStaleChunkRetry('GuestRoomPage', () => import('./components/Ops/stays/GuestRoomPage').then((module) => ({ default: module.GuestRoomPage })))
const GuestRequestStatusPage = lazyWithStaleChunkRetry('GuestRequestStatusPage', () => import('./components/Ops/stays/GuestRequestStatusPage').then((module) => ({ default: module.GuestRequestStatusPage })))
const EduEnquiryPage = lazyWithStaleChunkRetry('EduEnquiryPage', () => import('./components/Shared/EduEnquiryPage').then((module) => ({ default: module.EduEnquiryPage })))
const PublicBookingPage = lazyWithStaleChunkRetry('PublicBookingPage', () => import('./components/PublicBooking/PublicBookingPage').then((module) => ({ default: module.PublicBookingPage })))


activateDebugBypassFromUrl()
console.info('[boot] runtime mode', {
  isNoSignInPreviewActive: isNoSignInPreviewActive(),
  prod: import.meta.env.PROD,
  mode: import.meta.env.MODE,
  isNative: Capacitor.isNativePlatform(),
})
installStaleChunkReloadHandler()
startFeatureFlagSync()
// Set <html lang/dir> and NLS CSS vars before React mounts so the first
// paint is already in the user's locale — no LTR flash for RTL users, no
// wrong font metrics for CJK/Devanagari. NlsProvider re-applies these on
// locale change; the pre-mount call is idempotent.
applyStoredNlsLocaleToDocument()

const personalInMemberAppEnabled = (): boolean => featureFlag('personalInMemberApp')

if (typeof document !== 'undefined' && Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('isNativeApp')
}

const applyNativeInitialRoute = async () => {
  if (typeof window === 'undefined') return
  if (!Capacitor.isNativePlatform()) return
  try {
    const info = await CapacitorApp.getInfo()
    // Publish wrapper role synchronously for downstream reads (Login gating,
    // AuthContext branching to the native auth plugin, etc.).
    setWrapperRoleFromAppId(info.id)
    const targetPath = getNativeInitialRoute(info.id, window.location.pathname, PUBLIC_SITE_PATHS)
    if (targetPath) {
      window.history.replaceState(null, '', targetPath)
    }
  } catch {
    // App plugin not available — leave the SPA at '/'.
  }
}

// Capture deep-link / universal-link join tokens at app boot so the auth
// listener can claim them once the user signs in. Stripping the token from
// the URL keeps reloads idempotent.
if (typeof window !== 'undefined') {
  const token = parseJoinToken(window.location.href)
  if (token) {
    if (window.location.pathname.startsWith('/join/')) {
      window.history.replaceState({}, '', `/invite-review/${token}`)
    } else {
      stashPendingLinkToken(token)
    }
  }
}

type HelperRouteState = { kind: 'NONE' } | { kind: 'SHELL' } | { kind: 'INVITE'; token: string }
type MemberRouteState = { kind: 'NONE' } | { kind: 'ENTRY' } | { kind: 'SHELL'; route: MemberRoute }
type PublicRouteState = { kind: 'NONE' } | { kind: 'PUBLIC'; path: string }
type FieldPassRouteState = { kind: 'NONE' } | { kind: 'FIELD_PASS'; token: string }
type OwnerRouteState = { kind: 'NONE' } | { kind: 'OWNER'; subPath: string }

const OWNER_CONSOLE_PATH = '/sole-con-mana-ment-ge'

const detectOwnerRoute = (): OwnerRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (pathname === OWNER_CONSOLE_PATH || pathname.startsWith(`${OWNER_CONSOLE_PATH}/`)) {
    const rawSub = pathname.slice(OWNER_CONSOLE_PATH.length)
    const subPath = rawSub.startsWith('/') ? rawSub : `/${rawSub}`
    return { kind: 'OWNER', subPath: subPath || '/' }
  }
  return { kind: 'NONE' }
}

const useOwnerRoute = (): OwnerRouteState => {
  const [route, setRoute] = useState<OwnerRouteState>(detectOwnerRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectOwnerRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const detectFieldPassRoute = (): FieldPassRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/field-pass/')) return { kind: 'NONE' }
  const token = pathname.slice('/field-pass/'.length).split('/')[0]
  return token ? { kind: 'FIELD_PASS', token } : { kind: 'NONE' }
}

const useFieldPassRoute = (): FieldPassRouteState => {
  const [route, setRoute] = useState<FieldPassRouteState>(detectFieldPassRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectFieldPassRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type OutsideApprovalRouteState = { kind: 'NONE' } | { kind: 'OUTSIDE_APPROVAL'; token: string }

const detectOutsideApprovalRoute = (): OutsideApprovalRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/approve/')) return { kind: 'NONE' }
  const token = pathname.slice('/approve/'.length).split('/')[0]
  return token ? { kind: 'OUTSIDE_APPROVAL', token } : { kind: 'NONE' }
}

const useOutsideApprovalRoute = (): OutsideApprovalRouteState => {
  const [route, setRoute] = useState<OutsideApprovalRouteState>(detectOutsideApprovalRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectOutsideApprovalRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type LostClaimRouteState = { kind: 'NONE' } | { kind: 'LOST_CLAIM'; token: string }

const detectLostClaimRoute = (): LostClaimRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/claim/')) return { kind: 'NONE' }
  const token = pathname.slice('/claim/'.length).split('/')[0]
  return token ? { kind: 'LOST_CLAIM', token } : { kind: 'NONE' }
}

const useLostClaimRoute = (): LostClaimRouteState => {
  const [route, setRoute] = useState<LostClaimRouteState>(detectLostClaimRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectLostClaimRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type BmwHandoverRouteState = { kind: 'NONE' } | { kind: 'BMW_HANDOVER'; token: string }

const detectBmwHandoverRoute = (): BmwHandoverRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/bmw-handover/')) return { kind: 'NONE' }
  const token = pathname.slice('/bmw-handover/'.length).split('/')[0]
  return token ? { kind: 'BMW_HANDOVER', token } : { kind: 'NONE' }
}

const useBmwHandoverRoute = (): BmwHandoverRouteState => {
  const [route, setRoute] = useState<BmwHandoverRouteState>(detectBmwHandoverRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectBmwHandoverRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type SampleHandoverRouteState = { kind: 'NONE' } | { kind: 'SAMPLE_HANDOVER'; token: string }

const detectSampleHandoverRoute = (): SampleHandoverRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/sample-handover/')) return { kind: 'NONE' }
  const token = pathname.slice('/sample-handover/'.length).split('/')[0]
  return token ? { kind: 'SAMPLE_HANDOVER', token } : { kind: 'NONE' }
}

const useSampleHandoverRoute = (): SampleHandoverRouteState => {
  const [route, setRoute] = useState<SampleHandoverRouteState>(detectSampleHandoverRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectSampleHandoverRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type TeleConsentRouteState = { kind: 'NONE' } | { kind: 'TELE_CONSENT'; token: string }

const detectTeleConsentRoute = (): TeleConsentRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/tele-consent/')) return { kind: 'NONE' }
  const token = pathname.slice('/tele-consent/'.length).split('/')[0]
  return token ? { kind: 'TELE_CONSENT', token } : { kind: 'NONE' }
}

const useTeleConsentRoute = (): TeleConsentRouteState => {
  const [route, setRoute] = useState<TeleConsentRouteState>(detectTeleConsentRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectTeleConsentRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type OwnerFeedRouteState = { kind: 'NONE' } | { kind: 'OWNER_FEED'; token: string }

const detectOwnerFeedRoute = (): OwnerFeedRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (!pathname.startsWith('/owner/')) return { kind: 'NONE' }
  const token = pathname.slice('/owner/'.length).split('/')[0]
  return token ? { kind: 'OWNER_FEED', token } : { kind: 'NONE' }
}

const useOwnerFeedRoute = (): OwnerFeedRouteState => {
  const [route, setRoute] = useState<OwnerFeedRouteState>(detectOwnerFeedRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectOwnerFeedRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type ClientLinkRouteState = { kind: 'NONE' } | { kind: 'CLIENT_LINK'; token: string }

const detectClientLinkRoute = (): ClientLinkRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const token = extractClientLinkToken(window.location.pathname)
  return token ? { kind: 'CLIENT_LINK', token } : { kind: 'NONE' }
}

const useClientLinkRoute = (): ClientLinkRouteState => {
  const [route, setRoute] = useState<ClientLinkRouteState>(detectClientLinkRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectClientLinkRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

type SharedDeviceRouteState = { kind: 'NONE' } | { kind: 'SHARED_DEVICE' }

const detectSharedDeviceRoute = (): SharedDeviceRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  if (isSharedDevicePath(window.location.pathname)) {
    return { kind: 'SHARED_DEVICE' }
  }
  return { kind: 'NONE' }
}

const useSharedDeviceRoute = (): SharedDeviceRouteState => {
  const [route, setRoute] = useState<SharedDeviceRouteState>(detectSharedDeviceRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectSharedDeviceRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const useVerifyRoute = (): VerifyRouteState => {
  const [route, setRoute] = useState<VerifyRouteState>(() => detectVerifyRoute())
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectVerifyRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const useVerifyEvidenceRoute = (): boolean => {
  const [route, setRoute] = useState<boolean>(() => detectVerifyEvidenceRoute())
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectVerifyEvidenceRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const useAccountTransferConfirmRoute = (): AccountTransferConfirmRouteState => {
  const [route, setRoute] = useState<AccountTransferConfirmRouteState>(() => detectAccountTransferConfirmRoute())
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectAccountTransferConfirmRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const detectHelperRoute = (): HelperRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (pathname.startsWith('/invite/')) {
    const token = pathname.slice('/invite/'.length).split('/')[0]
    if (token) return { kind: 'INVITE', token }
  }
  if (pathname.startsWith('/helper')) return { kind: 'SHELL' }
  return { kind: 'NONE' }
}

const useHelperRoute = (): HelperRouteState => {
  const [route, setRoute] = useState<HelperRouteState>(detectHelperRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectHelperRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const detectMemberRoute = (): MemberRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  if (window.location.pathname === '/member') return { kind: 'ENTRY' }
  const route = parseMemberRoute(window.location.pathname)
  return route ? { kind: 'SHELL', route } : { kind: 'NONE' }
}

const useMemberRoute = (): MemberRouteState => {
  const [route, setRoute] = useState<MemberRouteState>(detectMemberRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectMemberRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const detectPublicRoute = (): PublicRouteState => {
  if (typeof window === 'undefined') return { kind: 'NONE' }
  const pathname = window.location.pathname
  if (PUBLIC_SITE_PATHS.has(pathname)) return { kind: 'PUBLIC', path: pathname }
  return { kind: 'NONE' }
}

const usePublicRoute = (): PublicRouteState => {
  const [route, setRoute] = useState<PublicRouteState>(detectPublicRoute)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handle = () => setRoute(detectPublicRoute())
    window.addEventListener('popstate', handle)
    return () => window.removeEventListener('popstate', handle)
  }, [])
  return route
}

const RedirectTo = ({ path }: { path: string }) => {
  const { t } = useNls()
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.pathname !== path) {
      window.history.replaceState(null, '', path)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }, [path])

  return <main className="grid min-h-dvh place-items-center bg-slate-950 p-6 text-sm text-slate-300">{t('NLSCapacitorRedirecting')}</main>
}

// Deliberately no bespoke "app-only route" surface — /helper* and /member*
// paths hit by a browser are indistinguishable from any other unknown URL.
// They flow through the top-level route classifier and land on NotFoundPage
// so we never advertise wrapper internals to random URL guessers.

let lastNavigationWasBack = false
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event: PopStateEvent) => {
    lastNavigationWasBack = event.isTrusted === true
  })
}

export const MemberProviderStack = ({ route }: { route: MemberRoute }) => {
  const { currentUser, userProfile, isAuthLoading } = useAuth()
  const {
    workspaces,
    activeWorkspaceId,
    rolesByWorkspaceId,
    isWorkspaceLoading,
    setAppMode,
    workspaceLoadingError,
  } = useWorkspace()
  const isAccessLostActive = useIsAccessLostPromptActive()
  const workspace = workspaces.find((candidate) =>
    route.slug === PERSONAL_SLUG_SEGMENT
      ? personalInMemberAppEnabled() && isPersonalWorkspace(candidate) && candidate.ownerId === currentUser?.uid
      : isTeamWorkspace(candidate) && candidate.slug === route.slug,
  )

  const lastSettledPathRef = useRef<string>(
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '',
  )

  useEffect(() => {
    if (!workspace) return
    if (activeWorkspaceId !== workspace.id) {
      const wasBack = lastNavigationWasBack
      lastNavigationWasBack = false
      const settled = lastSettledPathRef.current
      void requestWorkspaceSwitch({
        toWorkspaceId: workspace.id,
        source: 'URL',
        restoreOnStay: buildUrlRestoreOnStay({
          wasBack,
          settledPath: settled,
        }),
      }).then((res) => {
        if (res === 'SWITCHED') {
          setAppMode(isPersonalWorkspace(workspace) ? 'PERSONAL' : 'TEAM')
        }
      })
    } else {
      if (typeof window !== 'undefined') {
        lastSettledPathRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`
      }
      setAppMode(isPersonalWorkspace(workspace) ? 'PERSONAL' : 'TEAM')
    }
  }, [activeWorkspaceId, setAppMode, workspace])

  if (isAuthLoading) return (
    <FullscreenLoader waitForReady={isAuthLoading} onFinish={() => {}} />
  )
  if (!currentUser) return <RedirectTo path={resolveMemberShellFallbackPath(getWrapperRole())} />

  const isHelperUser = isHelperOnlyAccount({ userProfile, currentUser, workspaces, rolesByWorkspaceId })

  return (
    <MemberRoleGuard>
      {isHelperUser ? (
        <RedirectTo path="/helper/dashboard" />
      ) : isWorkspaceLoading && !workspace ? (
        <FullscreenLoader waitForReady={isWorkspaceLoading} error={workspaceLoadingError} onFinish={() => {}} />
      ) : !workspace && !isAccessLostActive ? (
        <RedirectTo path={resolveMemberShellFallbackPath(getWrapperRole())} />
      ) : !workspace ? (
        <FullscreenLoader waitForReady={true} onFinish={() => {}} />
      ) : (
        <WorkspaceScopedProviders workspaceId={workspace.id} shell="member">
          <TelemetryBoundary scope="route" routeName="member">
            <AppMember route={route} workspace={workspace} />
          </TelemetryBoundary>
          <MobileUpdateBridge />
          <MaintenanceBanner />
        </WorkspaceScopedProviders>
      )}
      <WorkspaceAccessLostSheet />
    </MemberRoleGuard>
  )
}

import { HelperProviderStack } from './shells/HelperProviderStack'

const NativeMemberEntry = () => {
  const { t } = useNls()
  const { currentUser, userProfile, isAuthLoading } = useAuth()
  const { workspaces, isWorkspaceLoading } = useWorkspace()
  const isHelperUser = isHelperOnlyAccount({ userProfile, currentUser, workspaces })

  useEffect(() => {
    if (!currentUser || isWorkspaceLoading || isHelperUser) return
    const memberWorkspace = workspaces.find((workspace) => isTeamWorkspace(workspace))
    const personalWorkspace = personalInMemberAppEnabled()
      ? resolvePersonalForUser(currentUser.uid, workspaces)
      : null
    const destination = memberWorkspace
      ? buildMemberPath({ workspace: memberWorkspace })
      : personalWorkspace
        ? buildMemberPath({ workspace: personalWorkspace })
        : null
    if (!destination) return
    window.history.replaceState(null, '', destination)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [currentUser, isWorkspaceLoading, isHelperUser, workspaces])

  if (isAuthLoading) return (
    <FullscreenLoader waitForReady={isAuthLoading} onFinish={() => {}} />
  )
  if (!currentUser) return <Login />
  if (isWorkspaceLoading) return (
    <FullscreenLoader waitForReady={isWorkspaceLoading} onFinish={() => {}} />
  )
  if (isHelperUser) return <RedirectTo path="/helper/dashboard" />

  return (
    <main className="grid min-h-dvh place-items-center bg-slate-950 p-6 text-center text-slate-200">
      <section className="max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-2xl">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t('NLSMemberNativeUnavailableEyebrow')}</p>
        <h1 className="mt-2 text-xl font-semibold">{t('NLSMemberNativeUnavailableTitle')}</h1>
        <p className="mt-2 text-sm text-slate-400">{t('NLSMemberNativeUnavailableBody')}</p>
      </section>
    </main>
  )
}

export function PlaceScanRouter({ token }: { token: string }): React.ReactElement {
  const { currentUser, isAuthLoading } = useAuth()
  const { isWorkspaceLoading } = useWorkspace()
  const [useGuestPage, setUseGuestPage] = useState<boolean | null>(null)

  useEffect(() => {
    if (isAuthLoading) return

    let active = true

    if (!currentUser) {
      import('./services/opsGuestRequestsService')
        .then((m) => m.getGuestRoomForm({ token }))
        .then(() => {
          if (!active) return
          setUseGuestPage(true)
        })
        .catch((err: unknown) => {
          if (!active) return
          const code = (err as { code?: string })?.code
          const message = String((err as { message?: string })?.message || '')
          const isNotFound = code === 'not-found' || message.includes('not-found')
          if (isNotFound) {
            setUseGuestPage(false)
          } else {
            setUseGuestPage(true)
          }
        })

      return () => {
        active = false
      }
    }

    if (isWorkspaceLoading) return

    import('./services/opsAttendanceQrService')
      .then((m) => m.getPlaceScanContext({ token }))
      .then(() => {
        if (!active) return
        setUseGuestPage(false)
      })
      .catch((err: unknown) => {
        if (!active) return
        const code = (err as { code?: string })?.code
        const message = String((err as { message?: string })?.message || '')
        const isPermissionDeniedOrNotFound =
          code === 'permission-denied' ||
          code === 'not-found' ||
          message.includes('permission-denied') ||
          message.includes('not-found')
        if (isPermissionDeniedOrNotFound) {
          setUseGuestPage(true)
        } else {
          setUseGuestPage(false)
        }
      })

    return () => {
      active = false
    }
  }, [currentUser, isAuthLoading, isWorkspaceLoading, token])

  if (useGuestPage === null) {
    return <div className="commandHubSuspense">Loading…</div>
  }

  if (useGuestPage) {
    return <GuestRoomPage token={token} />
  }

  return <PlaceScanPage token={token} />
}

export const WorkspaceTimelineBridge = () => {
  const { t } = useNls()
  const ownerRoute = useOwnerRoute()
  const fieldPassRoute = useFieldPassRoute()
  const outsideApprovalRoute = useOutsideApprovalRoute()
  const lostClaimRoute = useLostClaimRoute()
  const bmwHandoverRoute = useBmwHandoverRoute()
  const sampleHandoverRoute = useSampleHandoverRoute()
  const teleConsentRoute = useTeleConsentRoute()
  const ownerFeedRoute = useOwnerFeedRoute()
  const clientLinkRoute = useClientLinkRoute()
  const sharedDeviceRoute = useSharedDeviceRoute()
  const verifyRoute = useVerifyRoute()
  const verifyEvidenceRoute = useVerifyEvidenceRoute()
  const accountTransferRoute = useAccountTransferConfirmRoute()
  const helperRoute = useHelperRoute()
  const memberRoute = useMemberRoute()
  const publicRoute = usePublicRoute()
  const { currentUser, userProfile, isAuthLoading, wasLedgerDormant, isSessionLocked } = useAuth()
  const { workspaces, activeWorkspaceId, isWorkspaceLoading, needsOnboarding, workspaceLoadingError, refreshWorkspaces, activeRole, getWorkspaceRole } = useWorkspace()
  const isFamilyOnly = useIsFamilyOnly()
  const [initialSplashDone, setInitialSplashDone] = useState(false)
  const [setupSplashActive, setSetupSplashActive] = useState(false)
  const inviteReviewId = typeof window !== 'undefined' && window.location.pathname.startsWith('/invite-review/')
    ? window.location.pathname.slice('/invite-review/'.length).split('/')[0]
    : ''
  const isJoinRoute = typeof window !== 'undefined' && window.location.pathname === '/join'
  const pathname = typeof window === 'undefined' ? '/' : window.location.pathname
  const topLevelRouteKind = classifyTopLevelRoute({
    pathname,
    publicSitePaths: PUBLIC_SITE_PATHS,
    isNativeWrapper: Boolean(getWrapperRole()),
    // Browser access to app-only paths is limited to the existing local
    // mock/debug bypass. Production web requests still resolve to NotFound.
    allowBrowserPreviewRoutes: isNoSignInPreviewActive(),
  })
  // When a signed-out visitor lands on a console-scoped path we'd normally
  // gate behind Login, remember where they were headed so the AuthContext can
  // put them there once sign-in completes.
  useEffect(() => {
    if (currentUser) return
    if (!shouldStashForPostLogin(topLevelRouteKind)) return
    stashPendingRedirect(`${pathname}${typeof window !== 'undefined' ? window.location.search : ''}`)
  }, [currentUser, topLevelRouteKind, pathname])

  const isHelperAccount = isHelperOnlyAccount({ userProfile, currentUser, workspaces })

  // Wrapped-app route lock. Each APK is pinned to its documented surface:
  //   Helper  → /helper/{dashboard|calendar|meetings|chat|controls|profile}
  //   Member  → /member/{teamSlug}/{for-you|board|calendar|chat|you|controls}
  // Anything else (including a foldable unfold that re-renders against desktop
  // routes or a stray deep link) gets rewritten in place. See
  // agents/routing-and-state.md for the source-of-truth route inventory.
  useEffect(() => {
    const wrapperRole = getWrapperRole()
    const memberWorkspace = workspaces.find((workspace) => isTeamWorkspace(workspace))
    const redirectPath = resolveNativeRouteLockRedirect({
      wrapperRole,
      pathname: window.location.pathname,
      isAuthenticated: Boolean(currentUser),
      isHelperUser: isHelperAccount,
      firstTeamSlug: memberWorkspace?.slug ?? null,
      workspaces,
      openWorkspaceTarget,
    })
    if (!redirectPath) return
    window.history.replaceState(null, '', redirectPath)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [currentUser, userProfile, workspaces, helperRoute, memberRoute, isHelperAccount])

  useEffect(() => {
    if (isAuthLoading || !currentUser) return
    const activeWsRole = activeWorkspaceId ? (getWorkspaceRole(activeWorkspaceId) ?? activeRole) : activeRole
    const redirectPath = resolveFamilyConsoleRedirect({
      isAuthenticated: Boolean(currentUser),
      isHelper: isHelperAccount,
      isFamilyOnly,
      activeRole: isWorkspaceLoading ? null : activeWsRole,
      topLevelRouteKind,
    })
    if (!redirectPath) return
    if (typeof window === 'undefined') return
    if (window.location.pathname === redirectPath) return
    window.history.replaceState(null, '', redirectPath)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [currentUser, isAuthLoading, userProfile?.role, isFamilyOnly, isWorkspaceLoading, activeWorkspaceId, activeRole, getWorkspaceRole, topLevelRouteKind, isHelperAccount])

  if (topLevelRouteKind === 'unknown') {
    return <NotFoundPage path={pathname} />
  }

  if (topLevelRouteKind === 'shared-report') {
    const shareId = pathname.slice('/shared/reports/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading shared report…</div>}>
        <SharedReport shareId={decodeURIComponent(shareId)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'shared-report-card') {
    const shareId = pathname.slice('/shared/report-cards/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading shared report card…</div>}>
        <SharedReportCard shareId={decodeURIComponent(shareId)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'client-opt-out') {
    const token = pathname.slice('/o/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        <ClientOptOutPage token={decodeURIComponent(token)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'client-form') {
    const token = pathname.slice('/f/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        <ClientFormPage token={decodeURIComponent(token)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'client-quote') {
    const token = pathname.slice('/q/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        <ClientQuotePage token={decodeURIComponent(token)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'guest-request-status') {
    const token = pathname.slice('/gr/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        <GuestRequestStatusPage token={decodeURIComponent(token)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'place-scan') {
    const isPq = pathname.startsWith('/pq/')
    const prefix = isPq ? '/pq/' : '/q/'
    const token = pathname.slice(prefix.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        {isPq ? (
          <PlaceScanRouter token={decodeURIComponent(token)} />
        ) : (
          <PlaceScanPage token={decodeURIComponent(token)} />
        )}
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'edu-enquiry') {
    const token = pathname.slice('/enquire/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        <EduEnquiryPage token={decodeURIComponent(token)} />
      </Suspense>
    )
  }

  if (topLevelRouteKind === 'public-booking') {
    const token = pathname.slice('/book/'.length)
    return (
      <Suspense fallback={<div className="commandHubSuspense">Loading…</div>}>
        <PublicBookingPage token={decodeURIComponent(token)} />
      </Suspense>
    )
  }

  const isNativeWrappedApp = Boolean(getWrapperRole())
  const bootSplash = !initialSplashDone ? (
    <ConstellationSplash
      waitForReady={isNativeWrappedApp ? false : isAuthLoading}
      onFinish={() => setInitialSplashDone(true)}
      timing={BOOT_CONSTELLATION_SPLASH_TIMING}
    />
  ) : null

  if (topLevelRouteKind === 'family') {
    return (
      <>
        {bootSplash}
        <FamilyRoute />
      </>
    )
  }

  if (topLevelRouteKind === 'edu-ptm-join') {
    return (
      <>
        {bootSplash}
        <EduPtmJoinRoute />
      </>
    )
  }


  if ((import.meta.env.DEV || isNoSignInPreviewActive()) && (pathname === '/dev/actions' || pathname.startsWith('/dev/actions') || pathname === '/dev')) {
    return <DevActionsPanel />
  }

  if (ownerRoute.kind === 'OWNER') {
    return (
      <TelemetryBoundary scope="route" routeName="owner-console">
        <ToastProvider>
          <NotificationProvider>
            <OwnerConsole subPath={ownerRoute.subPath} />
            <ToastViewport />
          </NotificationProvider>
        </ToastProvider>
      </TelemetryBoundary>
    )
  }
  if (publicRoute.kind === 'PUBLIC') return <PublicSite path={publicRoute.path} />
  if (fieldPassRoute.kind === 'FIELD_PASS') return <>{bootSplash}<FieldPassLanding token={fieldPassRoute.token} /></>
  if (outsideApprovalRoute.kind === 'OUTSIDE_APPROVAL') return <>{bootSplash}<ApprovalLanding token={outsideApprovalRoute.token} /></>
  if (lostClaimRoute.kind === 'LOST_CLAIM') return <>{bootSplash}<ClaimLanding token={lostClaimRoute.token} /></>
  if (bmwHandoverRoute.kind === 'BMW_HANDOVER') return <>{bootSplash}<BmwHandoverLanding token={bmwHandoverRoute.token} /></>
  if (sampleHandoverRoute.kind === 'SAMPLE_HANDOVER') return <>{bootSplash}<SampleHandoverLanding token={sampleHandoverRoute.token} /></>
  if (teleConsentRoute.kind === 'TELE_CONSENT') return <>{bootSplash}<TeleConsentLanding token={teleConsentRoute.token} /></>
  if (ownerFeedRoute.kind === 'OWNER_FEED') return <>{bootSplash}<OwnerFeedLanding token={ownerFeedRoute.token} /></>
  if (clientLinkRoute.kind === 'CLIENT_LINK') return <>{bootSplash}<ClientLinkLanding token={clientLinkRoute.token} /></>
  if (sharedDeviceRoute.kind === 'SHARED_DEVICE' || topLevelRouteKind === 'shared-device') return <>{bootSplash}<SharedDeviceShell /></>
  if (verifyRoute.kind === 'VERIFY') return <>{bootSplash}<CertificateVerifyPage code={verifyRoute.code} /></>
  if (verifyEvidenceRoute) return <>{bootSplash}<VerifyEvidencePage /></>
  if (accountTransferRoute.kind === 'CONFIRM') return <>{bootSplash}<AccountTransferConfirmPage code={accountTransferRoute.code} confirmToken={accountTransferRoute.token} /></>

  if (helperRoute.kind === 'INVITE') return <>{bootSplash}<HelperInviteLanding token={helperRoute.token} /></>
  if (helperRoute.kind === 'SHELL') return <>{bootSplash}<HelperProviderStack /></>
  if (memberRoute.kind === 'ENTRY') return <>{bootSplash}<NativeMemberEntry /></>
  if (memberRoute.kind === 'SHELL') return <>{bootSplash}<MemberProviderStack route={memberRoute.route} /></>

  const setupSplash = setupSplashActive ? (
    <FullscreenLoader
      overlayText="Wait... your workspace is being set up!"
      waitForReady={needsOnboarding || !activeWorkspaceId}
      onFinish={() => setSetupSplashActive(false)}
    />
  ) : null

  const handleSetupStarting = () => setSetupSplashActive(true)

  // Auth resolved by the time the boot splash finishes; if it flips back to
  // loading later we just render whatever we can rather than blocking on a
  // "Checking session…" screen. The boot splash overlays whatever we render
  // here so the fade reveals it.
  if (inviteReviewId) return <>{bootSplash}<InviteReviewPage inviteId={inviteReviewId} /></>
  if (isJoinRoute) return <>{bootSplash}<JoinWithCodePage /></>
  if (!currentUser) return <>{bootSplash}{setupSplash}<Login /></>
  if (isSessionLocked) return <>{bootSplash}{setupSplash}<Login isLocked /></>
  if (currentUser) {
    const activeWsRole = activeWorkspaceId ? (getWorkspaceRole(activeWorkspaceId) ?? activeRole) : activeRole
    const familyRedirectPath = resolveFamilyConsoleRedirect({
      isAuthenticated: true,
      isHelper: isHelperAccount,
      isFamilyOnly,
      activeRole: isWorkspaceLoading ? null : activeWsRole,
      topLevelRouteKind,
    })
    if (familyRedirectPath) {
      return (
        <>
          {bootSplash}
          <main className="appRouteMessage">
            {t('NLSMemberOpeningFamily')}
          </main>
        </>
      )
    }

    const app = (
      <>
        {isWorkspaceLoading ? (
          <FullscreenLoader
            waitForReady={isWorkspaceLoading}
            onFinish={() => {}}
          />
        ) : isHelperAccount ? (
          <AccessDenied message={t('NLSCapacitorHelperAccessDenied')} />
        ) : workspaceLoadingError && !activeWorkspaceId ? (
          <main className="profileOnboardingGate" aria-label="Workspace load error">
            <section className="profileOnboardingCard">
              <p className="settingsEyebrow">Error</p>
              <h1>Could not load workspaces</h1>
              <p>{workspaceLoadingError}</p>
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem' }}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => { void refreshWorkspaces() }}
                >
                  {t('NLSCommonActionRetryLoadingWorkspaces') || 'Retry loading workspaces'}
                </Button>
              </div>
            </section>
          </main>
        ) : needsOnboarding ? (
          <>
            {setupSplash}
            <OnboardingGate
              initialMode={wasLedgerDormant ? undefined : 'TEAM'}
              onSetupStarting={handleSetupStarting}
              onSetupFailed={() => setSetupSplashActive(false)}
            />
          </>
        ) : !activeWorkspaceId ? (
          workspaces.filter((w) => !w.isPreview).length > 0 ? (
            <FullscreenLoader
              waitForReady={Boolean(!activeWorkspaceId)}
              onFinish={() => {}}
            />
          ) : (
            <>
              {setupSplash}
              <OnboardingGate
                initialMode="TEAM"
                onSetupStarting={handleSetupStarting}
                onSetupFailed={() => setSetupSplashActive(false)}
              />
            </>
          )
        ) : (
          <WorkspaceScopedProviders workspaceId={activeWorkspaceId} shell="web">
            <TelemetryBoundary scope="route" routeName="manage">
              <App />
            </TelemetryBoundary>
            <MobileUpdateBridge />
            <MaintenanceBanner />
            {setupSplash}
          </WorkspaceScopedProviders>
        )}
      </>
    )
    return (
      <>
        {bootSplash}
        {app}
        <WorkspaceAccessLostSheet />
        <ConsentNoticeDialog uid={currentUser?.uid} userProfile={userProfile} />
      </>
    )
  }
}

/**
 * Diagnostic: pings other tabs of this app via BroadcastChannel on boot. Any
 * duplicate tab logs a console warning so the developer knows they are paying
 * for a second Firestore session (each tab opens its own long-poll stream).
 * Zero UI, zero behavior change — pure telemetry.
 */
const wireDuplicateTabDiagnostic = () => {
  if (typeof BroadcastChannel === 'undefined') return
  try {
    const channel = new BroadcastChannel('timelining:tab-diagnostic')
    const nonce = Math.random().toString(36).slice(2)
    let siblingSeen = false
    channel.postMessage({ kind: 'hello', nonce })
    channel.onmessage = (event) => {
      const data = event.data as { kind?: string; nonce?: string } | null
      if (!data || data.nonce === nonce) return
      if (data.kind === 'hello') {
        channel.postMessage({ kind: 'ack', nonce })
      }
      if (!siblingSeen) {
        siblingSeen = true
        console.warn(
          '[timelining] Detected another tab running this app. Each tab opens ' +
          'its own Firestore session + long-poll stream, doubling network ' +
          'load. Close the other tab if it is not needed.',
        )
      }
    }
  } catch {
    /* BroadcastChannel unavailable or blocked — silently ignore. */
  }
}

const TelemetryUserContextBridge = () => {
  const { currentUser } = useAuth()
  const { activeWorkspaceId } = useWorkspace()
  useEffect(() => {
    telemetryLogger.setUserContext(currentUser?.uid ?? null, activeWorkspaceId ?? null)
  }, [currentUser?.uid, activeWorkspaceId])
  return null
}

const renderApp = () => {
  wireDuplicateTabDiagnostic()
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <TelemetryBoundary scope="root">
        <GlobalActionLoadingProvider>
          <NlsProvider>
            <ClientPreferencesProvider>
              <ApiConnectivityProvider>
                <PreviewProvider>
                  <AlivenessBridge />
                  <PreviewStatusBar />
                  <DebugSessionRecorder />
                  <AuthProvider>
                    <WorkspaceProvider>
                        <CreditsProvider>
                          <SubscriptionProvider>
                            <TelemetryUserContextBridge />
                            <DeferredEventOutboxBridge />
                            <LineLearningSignalFlushBridge />
                            <AutomationDispatchBridge />
                            <ProductAnalyticsRouteBridge />
                            <WorkspaceTimelineBridge />
                            <DebugStateProvidersBridge />
                            <ProfileDebugOverlay />
                          </SubscriptionProvider>
                        </CreditsProvider>
                    </WorkspaceProvider>
                  </AuthProvider>
                </PreviewProvider>
              </ApiConnectivityProvider>
            </ClientPreferencesProvider>
          </NlsProvider>
        </GlobalActionLoadingProvider>
        <GlobalNativeTitleUpgrader />
      </TelemetryBoundary>
    </StrictMode>,
  )
}

// Await the native-wrapper redirect (10-20ms round-trip to the JVM) before
// React mounts — otherwise the initial pathname read in useHelperRoute /
// useMemberRoute would race the replaceState and the app would flash the
// admin surface for one frame.
if (Capacitor.isNativePlatform()) {
  void applyNativeInitialRoute().then(renderApp)
} else {
  installConsoleHistoryAdapter()
  normalizeLegacyConsoleUrl()
  renderApp()
}
